// Hãng vận chuyển (BEST — cấu hình gộp vào thẻ, mở rộng khi chạm). Dùng ở Cài đặt › Hãng vận chuyển và trang
// Vận chuyển (tab đáy điện thoại). Kho lấy hàng mở từ link trong thẻ BEST, không còn nút riêng đứng lẻ trên đầu.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import { get } from '@/lib/api'
import { money, percent } from '@/lib/format'
import { cn } from '@/lib/utils'


interface Carrier {
  id: string; name: string; shortCode: string; color: string
  fee: number; eta: string; firstAttemptRate: number
  status: 'active' | 'beta' | 'soon'
  enabled: boolean; preferred: boolean; hasIntegration: boolean
  configured: boolean
}

export function CarriersPanel({ onGoToWarehouse }: { onGoToWarehouse?: () => void }) {
  const [expanded, setExpanded] = useState<string | null>('best')
  const { data, refetch } = useQuery({
    queryKey: ['carriers'],
    queryFn: () => get<{ carriers: Carrier[] }>('/carriers'),
  })

  const carriers = (data?.carriers || []).filter((c) => c.id === 'best')

  return (
    <div className="space-y-6">
      <ul className="space-y-2">
        {carriers.map((c) => {
          const isExpanded = expanded === c.id
          const canExpand = c.enabled && c.hasIntegration
          const needsConfig = c.enabled && c.hasIntegration && !c.configured
          return (
            <li key={c.id} className={cn(
              'border rounded-lg bg-raise overflow-hidden transition-colors',
              c.preferred ? 'border-accent/40 ring-2 ring-accent/15' : 'border-rule',
              needsConfig && 'border-warn/40',
            )}>
              {/* Tên một dòng, nhãn xuống dòng bên dưới, mũi tên ở góc — màn điện thoại 375px không bị ép chữ */}
              <div
                className={cn('flex items-start gap-3 p-4', canExpand && 'cursor-pointer hover:bg-paperSoft/40')}
                onClick={() => canExpand && setExpanded(isExpanded ? null : c.id)}
              >
                <div className="size-11 rounded-md flex items-center justify-center text-white text-xs font-medium shrink-0"
                  style={{ backgroundColor: c.color }}>{c.shortCode}</div>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-ink font-medium">{c.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {c.preferred && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-accent/12 text-accent">Mặc định</span>
                    )}
                    <StatusTag status={c.status} />
                    {c.hasIntegration && (
                      c.configured ? (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-ok/12 text-ok">Đã cấu hình</span>
                      ) : (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-warn/12 text-warn">Cần cấu hình</span>
                      )
                    )}
                    {!c.hasIntegration && c.enabled && (
                      <span className="text-xs text-muted">thủ công</span>
                    )}
                  </div>
                  <p className="text-xs text-muted mt-1.5 tabular-nums">
                    Từ {money(c.fee)} · {c.eta} · giao lần đầu {percent(c.firstAttemptRate, 0)}
                  </p>
                </div>
                {canExpand && (
                  <button
                    type="button"
                    className={cn(
                      '-mr-2 -mt-1 grid size-11 shrink-0 place-items-center rounded-md text-inkSoft hover:bg-paperSoft transition-transform',
                      isExpanded && 'rotate-180',
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      setExpanded(isExpanded ? null : c.id)
                    }}
                    aria-expanded={isExpanded}
                    aria-label="Cấu hình"
                  >
                    <ChevronDown className="size-4" />
                  </button>
                )}
              </div>

              {isExpanded && onGoToWarehouse && (
                <div className="border-t border-rule bg-paperSoft/30 p-5 text-sm text-inkSoft space-y-2">
                  <p>
                    <button type="button" className="text-accent underline" onClick={onGoToWarehouse}>Kho lấy hàng</button>
                  </p>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function StatusTag({ status }: { status: 'active' | 'beta' | 'soon' }) {
  const map = {
    active: { text: 'Sẵn sàng', cls: 'bg-ok/12 text-ok' },
    beta: { text: 'Thử nghiệm', cls: 'bg-warn/12 text-warn' },
    soon: { text: 'Sắp có', cls: 'bg-paperSoft text-muted' },
  }[status]
  return <span className={cn('text-xs px-1.5 py-0.5 rounded', map.cls)}>{map.text}</span>
}
